import { useQuery } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components } from '@/api/types/api-gateway';

// S6 Sensor Detail. Reads sensor diagnostics + thresholds from the
// gateway. R2d wires real persistence; the gateway already serves
// sample diagnostics so the UI can be built ahead of that.

export type SensorDiagnostics = components['schemas']['SensorDiagnostics'];
export type SensorThreshold = components['schemas']['SensorThreshold'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useSensorDiagnostics(clients: ApiClients, sensorId: string) {
  return useQuery({
    queryKey: ['sensor', sensorId, 'diagnostics'],
    queryFn: async (): Promise<SensorDiagnostics> => {
      const { data, error } = await clients.apiGateway.GET('/v1/sensors/{sensorId}', {
        params: { path: { sensorId } },
      });
      return unwrap(data, error, 'sensor');
    },
    staleTime: 30_000,
  });
}

export function useSensorThresholds(clients: ApiClients, sensorId: string) {
  return useQuery({
    queryKey: ['sensor', sensorId, 'thresholds'],
    queryFn: async (): Promise<SensorThreshold[]> => {
      const { data, error } = await clients.apiGateway.GET(
        '/v1/sensors/{sensorId}/thresholds',
        { params: { path: { sensorId } } },
      );
      const body = unwrap(data, error, 'thresholds');
      return body.items;
    },
    staleTime: 60_000,
  });
}
